import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { MessageService } from 'primeng/api';
import { FileUpload } from 'primeng/fileupload';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiService } from '../../services/api.service';
import { init } from '../editorconfig';

/** Revenue lifecycle charts: step → 7-stage aggregation + horizontal bar chart data */
interface RevenueLifecycleStageDef {
  label: string;
  matchKeys: string[];
}

const REVENUE_PRE_CONTRACT_LIFECYCLE: RevenueLifecycleStageDef[] = [
  {
    label: 'Need Identification & Approval',
    matchKeys: [
      'need generation',
      'apso approval',
      'need gen',
      'apso',
    ],
  },
  {
    label: 'SOTR & Feasibility Finalisation',
    matchKeys: [
      'finalisation of sotr',
      'sotr finalisation',
      'feasibility finalisation',
      'sotr',
      'aip',
    ],
  },
  {
    label: 'Vendor Identification & RFP Drafting',
    matchKeys: [
      'selection of firms',
      'selection of vendor',
      'vendors',
      'draft rfp',
    ],
  },
  {
    label: 'AoN & Committee Constitution',
    matchKeys: [
      'preparation of aon',
      'constitute toc',
      'committee constitution',
      'constitute tec',
      'constitute tnc',
      'constitute pc',
    ],
  },
  {
    label: 'Tendering Process',
    matchKeys: [
      'pre-bid meeting',
      'pre bid meeting',
      'tender opening',
      'issuance of rfp',
      'rfp vetting',
    ],
  },
  {
    label: 'Technical Evaluation & Compliance',
    matchKeys: [
      'sotr & rfp compliance matrix',
      'compliance matrix',
      'firm capacity assessment',
      'technical evaluation',
      'capacity assessment',
    ],
  },
  {
    label: 'Commercial Negotiation & Contract Award',
    matchKeys: [
      'financial sanction & contract',
      'cnc / pnc',
      'final offer upload',
      'technical negotiations',
      'benchmarking',
      'q bid opening',
      'bid opening and cst',
      'cnc',
      'pnc',
      'contract award',
    ],
  },
];

const REVENUE_EXECUTION_LIFECYCLE: RevenueLifecycleStageDef[] = [
  {
    label: 'Project Initiation',
    matchKeys: ['kick off meeting', 'kick off', 'kickoff'],
  },
  {
    label: 'Design & Documentation Approval',
    matchKeys: [
      'upload approved drawings',
      'approved drawings & documents',
      'qap & atp',
      'drawings & documents',
    ],
  },
  {
    label: 'Prototype Development (FAT Phase)',
    matchKeys: ['upload fat', 'fat phase', 'prototype development', 'fats'],
  },
  {
    label: 'Trials & Evaluation (HAT/SAT)',
    matchKeys: [
      'prototype hats or sats',
      'user evaluation trials',
      'hat/sat',
      'evaluation trials',
    ],
  },
  {
    label: 'Acceptance & Certification',
    matchKeys: [
      'formal acceptance of prototype',
      'end user certificate',
      'formal acceptance',
    ],
  },
  {
    label: 'Production Phase',
    matchKeys: ['bulk production'],
  },
  {
    label: 'Project Closure',
    matchKeys: ['project closure'],
  },
];

function revenueNorm(s: string): string {
  return (s || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/['']/g, "'")
    .trim();
}

function matchStepToLifecycleStage(
  stepName: string,
  stages: RevenueLifecycleStageDef[]
): number {
  const n = revenueNorm(stepName);
  if (!n) return -1;
  let bestLen = 0;
  let bestIdx = -1;
  for (let i = 0; i < stages.length; i++) {
    for (const k of stages[i].matchKeys) {
      const nk = revenueNorm(k);
      if (nk.length && n.includes(nk) && nk.length > bestLen) {
        bestLen = nk.length;
        bestIdx = i;
      }
    }
  }
  return bestIdx;
}

interface RevenueStepAggRow {
  name: string;
  pending: number;
  in_progress: number;
  completed: number;
  ote?: number;
  lte?: number;
  ste?: number;
  phase?: string;
}

const REVENUE_PRE_CONTRACT_STEP_SEQUENCE_DEFAULT = [
  'Need Generation',
  'APSO Approval',
  'Finalisation of SOTR',
  'AIP',
  'Selection of Firms/ Vendors',
  'Draft RFP',
  'Preparation Of AoN',
  'Constitute TOC, TEC, TNC, PC',
  'RFP Vetting',
  'Issuance of RFP',
  'Pre-bid Meeting',
  'Tender Opening',
  'SOTR & RFP compliance Matrix',
  'Technical Evaluation',
  'Firm Capacity Assessment',
  'Technical negotiations',
  'Benchmarking',
  'Q bid Opening and CST',
  'CNC / PNC & Final Offer Upload',
];

// Open Tender rule: "Selection of Firms/ Vendors" and "Issuance of RFP" swap order.
const REVENUE_PRE_CONTRACT_STEP_SEQUENCE_OPEN_TENDER = [
  'Need Generation',
  'APSO Approval',
  'Finalisation of SOTR',
  'AIP',
  'Issuance of RFP',
  'Draft RFP',
  'Preparation Of AoN',
  'Constitute TOC, TEC, TNC, PC',
  'RFP Vetting',
  'Selection of Firms/ Vendors',
  'Pre-bid Meeting',
  'Tender Opening',
  'SOTR & RFP compliance Matrix',
  'Technical Evaluation',
  'Firm Capacity Assessment',
  'Technical negotiations',
  'Benchmarking',
  'Q bid Opening and CST',
  'CNC / PNC & Final Offer Upload',
];

const REVENUE_POST_CONTRACT_STEP_SEQUENCE = [
  'Financial Sanction & Contract',
  'Kick Off Meeting',
  'Upload Approved Drawings & Documents and QAP & ATP',
  'Upload FATs',
  'Prototype HATs or SATs and User Evaluation Trials',
  'Formal Acceptance of Prototype Delivery',
  'End User Certificate',
  'Bulk Production',
  'Project Closure',
];

const REVENUE_STEP_ALIASES: Record<string, string[]> = {
  'Preparation Of AoN': ['Preparation of AoN'],
  'Upload Approved Drawings & Documents and QAP & ATP': [
    'Upload Approved Drawings and Documents',
    'Upload Approved QAP and ATP',
  ],
};

function getRevenueStepSequenceIndex(
  stepName: string,
  phase: string,
  useOpenTenderOrder: boolean
): number {
  const normalizedName = revenueNorm(stepName);
  const normalizedPhase = revenueNorm(phase);
  const sequence = normalizedPhase.includes('post contract') || normalizedPhase.includes('execution')
    ? REVENUE_POST_CONTRACT_STEP_SEQUENCE
    : (useOpenTenderOrder
      ? REVENUE_PRE_CONTRACT_STEP_SEQUENCE_OPEN_TENDER
      : REVENUE_PRE_CONTRACT_STEP_SEQUENCE_DEFAULT);

  for (let i = 0; i < sequence.length; i++) {
    if (revenueNorm(sequence[i]) === normalizedName) return i;
  }
  return Number.MAX_SAFE_INTEGER;
}

function namesEqualWithAliases(stepName: string, canonical: string): boolean {
  const nStep = revenueNorm(stepName);
  const nCanonical = revenueNorm(canonical);
  if (nStep === nCanonical) return true;
  const aliases = REVENUE_STEP_ALIASES[canonical] || [];
  return aliases.some((a) => revenueNorm(a) === nStep);
}

function getRevenueLifecycleStageIndex(
  stepName: string,
  phase: string,
  useOpenTenderOrder: boolean
): number {
  const normalizedPhase = revenueNorm(phase);
  const isExecution = normalizedPhase.includes('post contract') || normalizedPhase.includes('execution');
  if (isExecution) {
    if (namesEqualWithAliases(stepName, 'Financial Sanction & Contract') || namesEqualWithAliases(stepName, 'Kick Off Meeting')) return 0;
    if (namesEqualWithAliases(stepName, 'Upload Approved Drawings & Documents and QAP & ATP')) return 1;
    if (namesEqualWithAliases(stepName, 'Upload FATs')) return 2;
    if (namesEqualWithAliases(stepName, 'Prototype HATs or SATs and User Evaluation Trials')) return 3;
    if (namesEqualWithAliases(stepName, 'Formal Acceptance of Prototype Delivery') || namesEqualWithAliases(stepName, 'End User Certificate')) return 4;
    if (namesEqualWithAliases(stepName, 'Bulk Production')) return 5;
    if (namesEqualWithAliases(stepName, 'Project Closure')) return 6;
    return -1;
  }

  const preSeq = useOpenTenderOrder ? REVENUE_PRE_CONTRACT_STEP_SEQUENCE_OPEN_TENDER : REVENUE_PRE_CONTRACT_STEP_SEQUENCE_DEFAULT;
  const seqIdx = preSeq.findIndex((s) => namesEqualWithAliases(stepName, s));
  if (seqIdx < 0) return -1;
  if (seqIdx <= 1) return 0; // 1-2
  if (seqIdx <= 3) return 1; // 3-4
  if (seqIdx <= 5) return 2; // 5-6
  if (seqIdx <= 7) return 3; // 7-8
  if (seqIdx <= 11) return 4; // 9-12
  if (seqIdx <= 14) return 5; // 13-15
  return 6; // 16-19
}

interface RevenueLifecycleStepDetail {
  name: string;
  pending: number;
  in_progress: number;
  completed: number;
  ote: number;
  lte: number;
  ste: number;
  seq?: number;
}

function aggregateRevenueLifecycle(
  rows: RevenueStepAggRow[],
  stages: RevenueLifecycleStageDef[],
  unmappedStageIndex = 6,
  useOpenTenderOrder = false
): {
  labels: string[];
  pending: number[];
  inProgress: number[];
  completed: number[];
  details: RevenueLifecycleStepDetail[][];
  breakdownByStage: { OTE: number; LTE: number; STE: number }[];
} {
  const n = stages.length;
  const pending = Array(n).fill(0);
  const inProgress = Array(n).fill(0);
  const completed = Array(n).fill(0);
  const details: RevenueLifecycleStepDetail[][] = Array.from({ length: n }, () => []);
  const oteSum = Array(n).fill(0);
  const lteSum = Array(n).fill(0);
  const steSum = Array(n).fill(0);
  const stageStepSnapshots: Array<
    Array<{ pending: number; inProgress: number; completed: number; ote: number; lte: number; ste: number; seq: number; order: number }>
  > = Array.from({ length: n }, () => []);

  for (let order = 0; order < rows.length; order++) {
    const row = rows[order];
    const name = row.name || '-';
    const idx = getRevenueLifecycleStageIndex(name, row.phase || '', useOpenTenderOrder);
    if (idx < 0 || idx >= n) continue;
    const p = Number(row.pending ?? 0);
    const ip = Number(row.in_progress ?? 0);
    const c = Number(row.completed ?? 0);
    const ot = Number(row.ote ?? 0);
    const lt = Number(row.lte ?? 0);
    const st = Number(row.ste ?? 0);
    stageStepSnapshots[idx].push({
      pending: p,
      inProgress: ip,
      completed: c,
      ote: ot,
      lte: lt,
      ste: st,
      seq: getRevenueStepSequenceIndex(name, row.phase || '', useOpenTenderOrder),
      order,
    });
    details[idx].push({
      name,
      pending: p,
      in_progress: ip,
      completed: c,
      ote: ot,
      lte: lt,
      ste: st,
      seq: getRevenueStepSequenceIndex(name, row.phase || '', useOpenTenderOrder),
    });
  }

  for (let i = 0; i < n; i++) {
    const snapshots = [...stageStepSnapshots[i]].sort((a, b) =>
      a.seq === b.seq ? a.order - b.order : a.seq - b.seq
    );
    if (!snapshots.length) continue;

    // Rule:
    // 1) Start from the final (latest) step in this stage.
    // 2) If final has all zero counts, walk backward to nearest non-zero step.
    // 3) If all are zero, keep final step values (show zero).
    let picked = snapshots[snapshots.length - 1];
    for (let j = snapshots.length - 1; j >= 0; j--) {
      const s = snapshots[j];
      if (s.pending !== 0 || s.inProgress !== 0 || s.completed !== 0) {
        picked = s;
        break;
      }
    }

    pending[i] = picked.pending;
    inProgress[i] = picked.inProgress;
    completed[i] = picked.completed;
    oteSum[i] = picked.ote;
    lteSum[i] = picked.lte;
    steSum[i] = picked.ste;
  }

  return {
    labels: stages.map((s) => s.label),
    pending,
    inProgress,
    completed,
    details,
    breakdownByStage: stages.map((_, i) => ({
      OTE: oteSum[i],
      LTE: lteSum[i],
      STE: steSum[i],
    })),
  };
}

function isPostContractPhaseRow(row: RevenueStepAggRow): boolean {
  const ph = revenueNorm(row.phase || '');
  return (
    ph.includes('post contract') ||
    ph.includes('post-contract') ||
    ph.includes('execution')
  );
}

function splitRevenueProgressRows(rows: RevenueStepAggRow[]): {
  pre: RevenueStepAggRow[];
  exec: RevenueStepAggRow[];
} {
  const post = rows.filter(isPostContractPhaseRow);
  if (post.length > 0) {
    return {
      pre: rows.filter((r) => !isPostContractPhaseRow(r)),
      exec: post,
    };
  }
  const exec: RevenueStepAggRow[] = [];
  const pre: RevenueStepAggRow[] = [];
  for (const r of rows) {
    if (matchStepToLifecycleStage(r.name || '', REVENUE_EXECUTION_LIFECYCLE) >= 0) {
      exec.push(r);
    } else {
      pre.push(r);
    }
  }
  return { pre, exec };
}

@Component({
  selector: 'app-revenue-dashbaord',
  standalone: false,
  templateUrl: './revenue-dashbaord.component.html',
  styleUrl: './revenue-dashbaord.component.scss',
})
export class RevenueDashbaordComponent implements OnInit, AfterViewInit {
  proposals: any[] = [];
  filteredProposals: any[] = [];
  currentRole: string = 'doi';
  selectAll: boolean = false;

  stats = { total: 0, pending: 0, screening: 0, approved: 0 };

  filters = { category: '', status: '', priority: '', valuation: '' };
  
  // Proposal status tabs
  proposalStatusTabs = [
    { label: 'Work Done', status: 'initiated', active: false, tab: 1 },
    { label: 'Pending Work', status: 'in_review', active: false, tab: 1 },
  ];
  
  tableColumns = [
    { field: 'directorate', header: 'Directorate', filterType: 'text', filter: true },
    { field: 'name', header: 'Name', filterType: 'text', filter: true },
    { field: 'description', header: 'Description', filterType: 'text', filter: true },
    { field: 'steps', header: 'Steps', filterType: 'text' },
    { field: 'progress', header: 'Progress', filterType: 'text', filter: true },
    { field: 'proposal_status', header: 'Status', filterType: 'text', filter: true }
  ];
  
  currentPage: number = 1;
  activeTabIndex: number = 0;
  activePhaseId: any;
  selectedPhaseId: any;
  lazy: boolean = true;
  totalCount = 10;
  filteredRecentProposals: any[];
  proposalData = [];
  phases = [];
  schemes: any;
  currentSchemeId: any=1;
  activeStatusTabIndex: any = this.proposalStatusTabs[0];
  
  // Modal control
  detailsModalOpen = false;
  selectedProposal: any | null = null;
  userID: string;
  proposalForm: FormGroup;
  
  categoryOptions = [
    { label: 'To Move', value: 'To Move' },
    { label: 'To Fight', value: 'To Fight' },
    { label: 'To Float', value: 'To Float' },
  ];
  
  priorityOptions = [
    { label: 'High', value: 'High' },
    { label: 'Medium', value: 'Medium' },
    { label: 'Low', value: 'Low' },
  ];
  
  statusOptions = [
    { label: 'Launch', value: 'Launch' },
    { label: 'Duplicate', value: 'Duplicate' },
    { label: 'Reject', value: 'Reject' },
  ];
  
  isEditorDisabled: boolean = false;
  init: EditorComponent['init'] = init;
  draftSotr: File;
  draftSotrFile: File | null = null;
  supportingDocsFiles: File | null = null;
  
  private subscriptions = new Subscription();
  
  @ViewChild('draftSotrUpload') draftSotrUpload!: FileUpload;
  @ViewChild('fileUpload') fileUpload!: FileUpload;
  
  PDSGenId: any;
  screening: any;
  baseUrl: string = environment.APIUrl;
  needGenData: any;
  needGenId;
  
  @Input() isEditable: boolean = false;
  routeForm: FormGroup;
  roleList: any;
  userList: any;
  userListCopy: any[];
  unitList: any;
  selectedChallenges: any[];
  isEditMode = false;
  currentAction: 'view' | 'edit' = 'view';
  IRAremarks: string;
  fullscreenVisible = false;
  fullscreenChart: any = { type: '', data: {} };
  equipment: Equipment[] = [];
  system: System[] = [];
  filteredEquipment: Equipment[] = [];
  filteredSystem: System[] = [];

  chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
    },
  };

  // Chart Data
  chartData1 = {
    labels: ['2022', '2023', '2024'],
    datasets: [
      {
        label: 'Proposals Received',
        data: [120, 150, 180],
        backgroundColor: '#42A5F5',
      },
      { label: 'Completed', data: [90, 130, 160], backgroundColor: '#66BB6A' },
    ],
  };

  chartData2 = {
    labels: ['Idex', 'Adiiti', 'Prime', 'OC', 'Fauji'],
    datasets: [
      {
        label: 'Received',
        data: [50, 30, 40, 20, 25],
        backgroundColor: '#FFA726',
      },
      {
        label: 'In Progress',
        data: [20, 15, 10, 12, 8],
        backgroundColor: '#42A5F5',
      },
      {
        label: 'Completed',
        data: [25, 12, 20, 5, 15],
        backgroundColor: '#66BB6A',
      },
    ],
  };

  chartData3 = {
    labels: ['2022', '2023', '2024'],
    datasets: [
      {
        label: 'Funds Utilised (Cr)',
        data: [120, 180, 240],
        borderColor: '#42A5F5',
        fill: false,
        tension: 0.4,
      },
    ],
  };

  chartData4 = {
    labels: ['Proposal A', 'Proposal B', 'Proposal C', 'Proposal D'],
    datasets: [
      {
        data: [300, 150, 100, 80],
        backgroundColor: ['#42A5F5', '#66BB6A', '#FFA726', '#AB47BC'],
      },
    ],
  };

  chartData5 = {
    labels: ['AI/ML', 'Defence Tech', 'Space', 'Robotics'],
    datasets: [
      {
        data: [100, 150, 120, 80],
        backgroundColor: ['#42A5F5', '#FF7043', '#66BB6A', '#AB47BC'],
      },
    ],
  };

  chartData6 = {
    labels: ['Idex', 'Adiiti', 'Prime', 'OC', 'Fauji'],
    datasets: [
      {
        label: 'Proposal Value',
        data: [200, 150, 180, 100, 120],
        backgroundColor: '#42A5F5',
      },
      {
        label: 'Disbursed Funds',
        data: [150, 120, 160, 90, 100],
        backgroundColor: '#66BB6A',
      },
    ],
  };

  // ---------- Revenue Scheme Dashboard (analytical) ----------
  dashboardLoading = false;
  totalProposals = 0;
  totalContracts = 0;
  totalPreLaunch = 0;
  totalForLaunch = 0;
  totalShortClosed = 0;
  completedProposals = 0;
  schemeWiseGrant: Record<string, number> = {};
  schemeWiseProposal: Record<string, number> = {};
  schemeWisePrelaunch: Record<string, number> = {};
  schemeWiseForlaunch: Record<string, number> = {};
  schemeWiseShortClosed: Record<string, number> = {};
  schemeWiseCompleted: Record<string, number> = {};

  readonly revenueStageLabels = [
    'Need & Approval Initiation',
    'Pre-RFP Readiness',
    'RFP & Tendering',
    'Technical Evaluation',
    'Commercial & Contracting',
    'Prototype & Trials',
    'Production & Closure',
  ];
  readonly revenueStageKeys = [
    'NEED_APPROVAL',
    'PRE_RFP',
    'RFP_TENDERING',
    'TECH_EVAL',
    'COMMERCIAL',
    'PROTOTYPE_TRIALS',
    'PRODUCTION_CLOSURE',
  ];
  readonly executionStageLabels = [
    'Kick Off',
    'Drawings / QAP / ATP',
    'FATs',
    'Trials',
    'Acceptance',
    'End User Certificate',
    'Bulk Production',
    'Project Closure',
  ];

  stageProgressChart: any = {
    labels: [] as string[],
    datasets: [
      { label: 'Pending', data: [] as number[], backgroundColor: '#94A3B8' },
      { label: 'In Progress', data: [] as number[], backgroundColor: '#F59E0B' },
      { label: 'Completed', data: [] as number[], backgroundColor: '#10B981' },
    ],
  };
  /** Legacy per-step breakdown (unused after lifecycle aggregation) */
  stageProgressBreakdown: { OTE: number; LTE: number; STE: number }[][] = [];
  /** Step-level rows merged into each lifecycle stage (for tooltips) */
  stageProgressLifecycleDetails: RevenueLifecycleStepDetail[][] = [];
  executionProgressLifecycleDetails: RevenueLifecycleStepDetail[][] = [];
  stageProgressBreakdownLifecycle: { OTE: number; LTE: number; STE: number }[] = [];
  executionProgressBreakdownLifecycle: { OTE: number; LTE: number; STE: number }[] = [];

  private revenueLifecycleBarOptions(
    chart: 'pre' | 'exec'
  ): Record<string, unknown> {
    const comp = this;
    const details =
      chart === 'pre'
        ? () => comp.stageProgressLifecycleDetails
        : () => comp.executionProgressLifecycleDetails;
    const breakdown =
      chart === 'pre'
        ? () => comp.stageProgressBreakdownLifecycle
        : () => comp.executionProgressBreakdownLifecycle;
    return {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 6, bottom: 8, left: 8, right: 20 } },
      plugins: {
        legend: { position: 'top' },
        tooltip: {
          bodySpacing: 4,
          padding: 12,
          maxWidth: 380,
          callbacks: {
            afterBody: (tooltipItems: any[]) => {
              if (!tooltipItems?.length) return [] as string[];
              const idx = tooltipItems[0].dataIndex as number;
              const steps = [...(details()[idx] || [])].sort((a, b) => {
                const sa = typeof a.seq === 'number' ? a.seq : Number.MAX_SAFE_INTEGER;
                const sb = typeof b.seq === 'number' ? b.seq : Number.MAX_SAFE_INTEGER;
                if (sa === sb) return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
                return sa - sb;
              });
              const lines: string[] = [];
              if (steps.length) {
                lines.push('Steps in this stage:');
                const maxSteps = 14;
                const show = steps.slice(0, maxSteps);
                for (const s of show) {
                  lines.push(
                    `• ${s.name}: Pending ${s.pending}, In progress ${s.in_progress}, Completed ${s.completed}`
                  );
                }
                if (steps.length > maxSteps) {
                  lines.push(`… and ${steps.length - maxSteps} more step(s)`);
                }
              }
              const b = breakdown()[idx];
              if (b && (b.OTE > 0 || b.LTE > 0 || b.STE > 0)) {
                lines.push(
                  `Category mix (stage): OTE ${b.OTE} · LTE ${b.LTE} · STE ${b.STE}`
                );
              }
              return lines;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          beginAtZero: true,
          grid: { color: 'rgba(148, 163, 184, 0.25)' },
          ticks: { precision: 0, font: { size: 11 } },
          title: { display: true, text: 'Count', font: { size: 11 } },
        },
        y: {
          stacked: true,
          reverse: false,
          grid: { display: false },
          ticks: {
            autoSkip: false,
            font: { size: 10.5 },
            padding: 8,
          },
          border: { display: false },
        },
      },
      datasets: {
        bar: {
          categoryPercentage: 0.78,
          barPercentage: 0.9,
        },
      },
    };
  }

  get stageProgressChartOptions(): Record<string, unknown> {
    return this.revenueLifecycleBarOptions('pre');
  }

  get executionProgressChartOptions(): Record<string, unknown> {
    return this.revenueLifecycleBarOptions('exec');
  }

  categoryDoughnutChart: any = {
    labels: ['OTE', 'LTE', 'STE'],
    datasets: [{ data: [45, 32, 23], backgroundColor: ['#0EA5E9', '#10B981', '#8B5CF6'], hoverOffset: 4 }],
  };
  statusDoughnutChart: any = {
    labels: ['Under Process', 'APSO Approved', 'RFP Issued', 'Contract Awarded', 'Short Closed', 'Completed'],
    datasets: [{ data: [18, 12, 10, 8, 5, 22], backgroundColor: ['#64748B', '#8B5CF6', '#F59E0B', '#2563EB', '#94A3B8', '#10B981'], hoverOffset: 4 }],
  };
  doughnutOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom', labels: { padding: 12, usePointStyle: true } } },
    cutout: '65%',
  };

  stageCategoryBarChart: any = {
    labels: [] as string[],
    datasets: [
      { label: 'OTE', data: [] as number[], backgroundColor: '#0EA5E9' },
      { label: 'LTE', data: [] as number[], backgroundColor: '#10B981' },
      { label: 'STE', data: [] as number[], backgroundColor: '#8B5CF6' },
    ],
  };
  groupedBarOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'top' } },
    scales: {
      x: { grid: { display: false }, ticks: { maxRotation: 45, font: { size: 11 } } },
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };

  approvalFunnelChart: any = {
    labels: ['Total Proposals', 'APSO Approved', 'AIP Completed', 'RFP Issued', 'Tech Eval Completed', 'Contract Awarded', 'Prototype Accepted', 'Completed'],
    datasets: [{ label: 'Count', data: [75, 58, 52, 40, 28, 18, 12, 8], backgroundColor: '#3B82F6' }],
  };
  funnelBarOptions: any = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { beginAtZero: true, ticks: { precision: 0 } },
      y: { grid: { display: false } },
    },
  };

  executionProgressChart: any = {
    labels: [] as string[],
    datasets: [
      { label: 'Pending', data: [] as number[], backgroundColor: '#94A3B8' },
      { label: 'In Progress', data: [] as number[], backgroundColor: '#F59E0B' },
      { label: 'Completed', data: [] as number[], backgroundColor: '#10B981' },
    ],
  };

  delayChart: any = {
    labels: ['Within Timeline', 'Delayed 1–30 days', 'Delayed 31–60 days', 'Delayed >60 days'],
    datasets: [{ data: [42, 18, 8, 7], backgroundColor: ['#10B981', '#F59E0B', '#F97316', '#EF4444'], hoverOffset: 4 }],
  };

  proposalStageTrackingData: any[] = [];
  stageTrackingGlobalFilter = '';
  stageTrackingGlobalFilterFields = ['proposal_id', 'proposal_title', 'category', 'current_stage'];
  revenueStageFilters = {
    proposal_title: '', category: '', current_stage: '', overall_status: '', last_updated: '', delay_days: ''
  };
  revenueStageSortField = '';
  revenueStageSortOrder: 1 | -1 = 1;
  filteredRevenueStageData: any[] = [];
  revenueStageTablePageSize = 10;
  revenueStageTableCurrentPage = 1;
  revenueStageTableTotalPages = 0;
  revenueStageTableStartEntry = 0;
  revenueStageTableEndEntry = 0;
  revenueStageTablePaginatedData: any[] = [];
  revenueStageTablePageSizeOptions = [5, 10, 25, 50];
  revenueManageFilters = { proposal_name: '', nomenclature: '', submitted_date: '' };
  revenueManageSortField = '';
  revenueManageSortOrder: 1 | -1 = 1;
  revenueManageFilteredProposals: any[] = [];
  revenueManagePageSize = 10;
  revenueManageCurrentPage = 1;
  revenueManageTotalPages = 0;
  revenueManageStartEntry = 0;
  revenueManageEndEntry = 0;
  revenueManagePaginatedData: any[] = [];
  revenueManagePageSizeOptions = [5, 10, 25, 50];

  schemeID: any=1;
  currentRoleName: any;
  selectedYear: number = new Date().getFullYear();
  yearOptions: any[] = [];
  statusCards: any[] = [];
  uploadedFiles: any[] = [];
  
  constructor(
    private messageService: MessageService,
    private api: ApiService,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private router: Router
  ) {
    const role = sessionStorage.getItem('user_role_id');
    this.userID = role;
    
    this.routeForm = this.fb.group({
      step: [null, Validators.required],
      role: [null, Validators.required],
      user: [null, Validators.required],
      level: [null, Validators.required],
      approved: [false]
    });
  }

  ngOnInit(): void {
    this.applyRevenueStageFilter();
    this.applyRevenueManageFilter();
    this.switchRole('doi');
    const currentYear = new Date().getFullYear();
    this.yearOptions = Array.from({ length: 6 }, (_, i) => ({
      label: (currentYear - i).toString(),
      value: currentYear - i,
    }));
    
    this.gettableData();
    this.proposalForm = this.fb.group({
      nomenclature: ['', Validators.required],
      equipment: [''],
      system: [''],
      qtyReqd: ['', Validators.required],
      qtyPrototype: ['', Validators.required],
      qtyBulk: ['', Validators.required],
      estimatedDevelopmentCost: [''],
      estimatedUnitCost: [''],
      llp: [''],
      poev: [''],
      consignee: [''],
      technologyInsertion: [''],
      justificationText: [''],
      exportPotential: [''],
      approvalOfSOTRs: [''],
    });
    
    this.route.queryParams.subscribe((params) => {
      const schemeId = params['scheme_id'];
      this.schemeID = schemeId || 1;
      this.getProposal('work_on');
      this.getDashboardData(this.selectedYear);
      this.loadRevenueSchemeDashboard();
      this.loadRevenueStageTableData();
    });
  }

  /** Load all revenue dashboard charts and KPIs from base_url/revenue/stage-summary/?scheme_id=1 */
  loadRevenueSchemeDashboard(): void {
    this.dashboardLoading = true;
    const schemeId = this.schemeID ?? 1;
    this.api.getAPI(`/revenue/stage-summary/?scheme_id=${schemeId}`).subscribe({
      next: (res: any) => {
        const data = res.data ?? res;
        const emptyScheme = { OTE: 0, LTE: 0, STE: 0 };

        // KPIs from data.total_proposals, data.apso_approved, data.total_contracts, etc.
        const tp = data.total_proposals;
        this.totalProposals = Number(tp?.count ?? data.total_proposal ?? 0);
        this.schemeWiseProposal = tp && typeof tp === 'object' ? { OTE: Number(tp.OTE ?? 0), LTE: Number(tp.LTE ?? 0), STE: Number(tp.STE ?? 0) } : { ...emptyScheme };

        const tc = data.total_contracts;
        let contractCount = 0;
        let schemeWiseGrantFromContracts = emptyScheme;
        if (Array.isArray(tc) && tc.length > 0) {
          const flat = Array.isArray(tc[0]) ? tc[0] : tc;
          contractCount = flat.reduce((s: number, row: any) => s + Number(row?.contract_count ?? 0), 0);
          const noVendor = flat.find((r: any) => r?.vendor == null);
          if (noVendor) {
            schemeWiseGrantFromContracts = { OTE: Number(noVendor.ote ?? 0), LTE: Number(noVendor.lte ?? 0), STE: Number(noVendor.ste ?? 0) };
          }
        }
        this.totalContracts = contractCount;
        this.schemeWiseGrant = { ...emptyScheme, ...schemeWiseGrantFromContracts };

        const apso = data.apso_approved;
        this.totalPreLaunch = Number(apso?.count ?? data.total_pre_launch ?? 0);
        this.schemeWisePrelaunch = apso && typeof apso === 'object' ? { OTE: Number(apso.OTE ?? 0), LTE: Number(apso.LTE ?? 0), STE: Number(apso.STE ?? 0) } : { ...emptyScheme };

        const rfp = data.rfp_issued;
        this.totalForLaunch = Number(rfp?.count ?? data.total_for_launch ?? 0);
        this.schemeWiseForlaunch = rfp && typeof rfp === 'object' ? { OTE: Number(rfp.OTE ?? 0), LTE: Number(rfp.LTE ?? 0), STE: Number(rfp.STE ?? 0) } : { ...emptyScheme };

        const sc = data.short_closed;
        this.totalShortClosed = Number(sc?.count ?? data.total_short_closed ?? 0);
        this.schemeWiseShortClosed = sc && typeof sc === 'object' ? { OTE: Number(sc.OTE ?? 0), LTE: Number(sc.LTE ?? 0), STE: Number(sc.STE ?? 0) } : { ...emptyScheme };

        const comp = data.completed_proposals;
        this.completedProposals = Number(comp?.count ?? data.completed_proposal ?? 0);
        this.schemeWiseCompleted = comp && typeof comp === 'object' ? { OTE: Number(comp.OTE ?? 0), LTE: Number(comp.LTE ?? 0), STE: Number(comp.STE ?? 0) } : { ...emptyScheme };

        // Stage progress: aggregate step-level API data into 7 pre-contract + 7 execution lifecycle stages
        const stageProgress: any[] =
          res.stage_progress ?? data.stage_progress ?? [];
        const stepRows: RevenueStepAggRow[] = stageProgress.map((s: any) => ({
          name: (
            (s.name || s.step_name || '').trim() ||
            s.step_mapping__phase__name ||
            '-'
          ).toString(),
          pending: Number(s.pending ?? 0),
          in_progress: Number(s.in_progress ?? 0),
          completed: Number(s.completed ?? 0),
          ote: Number(s.ote ?? 0),
          lte: Number(s.lte ?? 0),
          ste: Number(s.ste ?? 0),
          phase: (s.step_mapping__phase__name || s.phase || '').toString(),
        }));

        const { pre, exec } = splitRevenueProgressRows(stepRows);
        const preAgg = aggregateRevenueLifecycle(
          pre,
          REVENUE_PRE_CONTRACT_LIFECYCLE,
          6,
          true
        );
        const execAgg = aggregateRevenueLifecycle(
          exec,
          REVENUE_EXECUTION_LIFECYCLE,
          6,
          false
        );

        const preLabels = [...preAgg.labels];
        this.stageProgressChart = {
          labels: preLabels,
          datasets: [
            {
              label: 'Pending',
              data: [...preAgg.pending],
              backgroundColor: '#94A3B8',
            },
            {
              label: 'In Progress',
              data: [...preAgg.inProgress],
              backgroundColor: '#F59E0B',
            },
            {
              label: 'Completed',
              data: [...preAgg.completed],
              backgroundColor: '#10B981',
            },
          ],
        };
        this.stageProgressLifecycleDetails = preAgg.details;
        this.stageProgressBreakdownLifecycle = preAgg.breakdownByStage;
        this.stageProgressBreakdown = preAgg.labels.map(() => [
          { OTE: 0, LTE: 0, STE: 0 },
          { OTE: 0, LTE: 0, STE: 0 },
          { OTE: 0, LTE: 0, STE: 0 },
        ]);

        this.stageCategoryBarChart = {
          labels: [...preLabels],
          datasets: [
            {
              label: 'OTE',
              data: preAgg.breakdownByStage.map((b) => b.OTE),
              backgroundColor: '#0EA5E9',
            },
            {
              label: 'LTE',
              data: preAgg.breakdownByStage.map((b) => b.LTE),
              backgroundColor: '#10B981',
            },
            {
              label: 'STE',
              data: preAgg.breakdownByStage.map((b) => b.STE),
              backgroundColor: '#8B5CF6',
            },
          ],
        };

        const execLabels = [...execAgg.labels];
        this.executionProgressChart = {
          labels: execLabels,
          datasets: [
            {
              label: 'Pending',
              data: [...execAgg.pending],
              backgroundColor: '#94A3B8',
            },
            {
              label: 'In Progress',
              data: [...execAgg.inProgress],
              backgroundColor: '#F59E0B',
            },
            {
              label: 'Completed',
              data: [...execAgg.completed],
              backgroundColor: '#10B981',
            },
          ],
        };
        this.executionProgressLifecycleDetails = execAgg.details;
        this.executionProgressBreakdownLifecycle = execAgg.breakdownByStage;

        // Proposal-wise stage tracking table is loaded from /revenue/stage-summary/table-data/ (see loadRevenueStageTableData)

        if (res.category_doughnut) {
          this.categoryDoughnutChart = { ...this.categoryDoughnutChart, ...res.category_doughnut };
        }
        if (res.status_doughnut) {
          this.statusDoughnutChart = { ...this.statusDoughnutChart, ...res.status_doughnut };
        }
        if (res.approval_funnel) {
          this.approvalFunnelChart = { ...this.approvalFunnelChart, ...res.approval_funnel };
        }
        if (res.delay_chart) {
          this.delayChart = { ...this.delayChart, ...res.delay_chart };
        }
        this.dashboardLoading = false;
      },
      error: (err) => {
        console.error('Error loading revenue stage-summary:', err);
        this.dashboardLoading = false;
        const preLbl = REVENUE_PRE_CONTRACT_LIFECYCLE.map((s) => s.label);
        const execLbl = REVENUE_EXECUTION_LIFECYCLE.map((s) => s.label);
        const z7 = () => Array(7).fill(0);
        this.stageProgressChart = {
          labels: preLbl,
          datasets: [
            { label: 'Pending', data: z7(), backgroundColor: '#94A3B8' },
            { label: 'In Progress', data: z7(), backgroundColor: '#F59E0B' },
            { label: 'Completed', data: z7(), backgroundColor: '#10B981' },
          ],
        };
        this.executionProgressChart = {
          labels: execLbl,
          datasets: [
            { label: 'Pending', data: z7(), backgroundColor: '#94A3B8' },
            { label: 'In Progress', data: z7(), backgroundColor: '#F59E0B' },
            { label: 'Completed', data: z7(), backgroundColor: '#10B981' },
          ],
        };
        this.stageProgressLifecycleDetails = Array(7)
          .fill(null)
          .map(() => []);
        this.executionProgressLifecycleDetails = Array(7)
          .fill(null)
          .map(() => []);
        this.stageProgressBreakdownLifecycle = Array(7)
          .fill(null)
          .map(() => ({ OTE: 0, LTE: 0, STE: 0 }));
        this.executionProgressBreakdownLifecycle = Array(7)
          .fill(null)
          .map(() => ({ OTE: 0, LTE: 0, STE: 0 }));
      },
    });
  }

  /** Load Proposal-wise Stage Tracking table from base_url/revenue/stage-summary/table-data/?scheme_id=1 */
  loadRevenueStageTableData(): void {
    const schemeId = this.schemeID ?? 1;
    this.api.getAPI(`/revenue/stage-summary/table-data/?scheme_id=${schemeId}`).subscribe({
      next: (res: any) => {
        const raw = res.data ?? res.results ?? res;
        const rows = Array.isArray(raw) ? raw : (raw?.results && Array.isArray(raw.results) ? raw.results : []);
        this.proposalStageTrackingData = rows.map((p: any, i: number) => ({
          proposal_title: p.proposal_title ?? p.proposal_name ?? p.name ?? p.title ?? p.proposalTitle ?? '-',
          category: (() => {
            const v = (p.category ?? p.scheme_name ?? p.schemeName ?? p.category_type ?? '').toString().trim();
            return !v || v.toLowerCase() === 'null' || v.toLowerCase() === 'undefined' ? '-' : v;
          })(),
          current_stage: p.current_stage ?? p.stage ?? p.currentStage ?? '-',
          overall_status: p.overall_status ?? p.status ?? p.overallStatus ?? '-',
          NEED_APPROVAL: p.NEED_APPROVAL ?? p.need_approval ?? p.needApproval ?? p.APSO ?? 'Not Started',
          PRE_RFP: p.PRE_RFP ?? p.pre_rfp ?? p.preRfp ?? p.RFP ?? 'Not Started',
          RFP_TENDERING: p.RFP_TENDERING ?? p.rfp_tendering ?? p.rfpTendering ?? p.tendor ?? 'Not Started',
          TECH_EVAL: p.TECH_EVAL ?? p.tech_eval ?? p.techEval ?? p.evaluation ?? 'Not Started',
          COMMERCIAL: p.COMMERCIAL ?? p.commercial ?? p.contract ?? 'Not Started',
          PROTOTYPE_TRIALS: p.PROTOTYPE_TRIALS ?? p.prototype_trials ?? p.prototypeTrials ?? p.prototype ?? 'Not Started',
          PRODUCTION_CLOSURE: p.PRODUCTION_CLOSURE ?? p.production_closure ?? p.productionClosure ?? p.closure ?? 'Not Started',
          last_updated: p.last_updated ?? p.last_updated_on ?? p.updated_at ?? p.updatedAt ? new Date(p.last_updated ?? p.last_updated_on ?? p.updated_at ?? p.updatedAt) : new Date(),
          delay_days: p.delay_days ?? p.delay ?? p.delayDays ?? 0,
        }));
        this.applyRevenueStageFilter();
      },
      error: (err) => {
        console.error('Error loading revenue stage-summary table-data:', err);
        this.proposalStageTrackingData = [];
        this.applyRevenueStageFilter();
      },
    });
  }

  getCurrentStage(proposal: any): string {
    return proposal?.current_stage || '-';
  }

  getOverallStatus(proposal: any): string {
    return proposal?.overall_status || '-';
  }

  getStageStatus(proposal: any, stageKey: string): string {
    const v = proposal?.[stageKey];
    return v === undefined || v === null ? 'Not Started' : String(v);
  }

  /** Map status text to stage-dot CSS class for legend-aligned dots */
  getStageDotClass(status: string): string {
    const n = (status || '').toLowerCase();
    if (n === 'completed') return 'stage-dot stage-dot-completed';
    if (n === 'in progress' || n === 'on-going' || n === 'ongoing' || n === 'in review' || n === 'initiated') return 'stage-dot stage-dot-progress';
    if (n === 'pending' || n === 'not started') return 'stage-dot stage-dot-pending';
    return 'stage-dot stage-dot-pending';
  }

  getStageTagSeverity(value: string): 'success' | 'info' | 'warning' | 'secondary' | 'danger' {
    if (!value) return 'secondary';
    const v = (value as string).toLowerCase();
    if (v === 'completed') return 'success';
    if (v === 'in progress') return 'info';
    if (v === 'pending') return 'warning';
    return 'secondary';
  }

  getDaysInCurrentStage(proposal: any): number | string {
    const d = proposal?.delay_days;
    return d !== undefined && d !== null ? d : '-';
  }

  applyRevenueStageFilter(): void {
    const f = this.revenueStageFilters;
    const list = this.proposalStageTrackingData || [];
    this.filteredRevenueStageData = list.filter((p: any) => {
      const proposalTitle = (p.proposal_title ?? '').toString().toLowerCase();
      const category = (p.category ?? '').toString().toLowerCase();
      const currentStage = (p.current_stage ?? '').toString().toLowerCase();
      const overallStatus = (p.overall_status ?? '').toString().toLowerCase();
      const lastUpdated = (p.last_updated ? new Date(p.last_updated).toLocaleDateString('en-GB') : '').toLowerCase();
      const delayDays = (this.getDaysInCurrentStage(p) ?? '').toString().toLowerCase();
      if (f.proposal_title && !proposalTitle.includes(f.proposal_title.trim().toLowerCase())) return false;
      if (f.category && !category.includes(f.category.trim().toLowerCase())) return false;
      if (f.current_stage && !currentStage.includes(f.current_stage.trim().toLowerCase())) return false;
      if (f.overall_status && !overallStatus.includes(f.overall_status.trim().toLowerCase())) return false;
      if (f.last_updated && !lastUpdated.includes(f.last_updated.trim().toLowerCase())) return false;
      if (f.delay_days && !delayDays.includes(f.delay_days.trim().toLowerCase())) return false;
      return true;
    });
    this.revenueStageTableCurrentPage = 1;
    this.setupRevenueStagePagination();
  }

  sortRevenueStage(field: string): void {
    if (this.revenueStageSortField === field) {
      this.revenueStageSortOrder = (this.revenueStageSortOrder === 1 ? -1 : 1) as 1 | -1;
    } else {
      this.revenueStageSortField = field;
      this.revenueStageSortOrder = 1;
    }
    this.setupRevenueStagePagination();
  }

  setupRevenueStagePagination(): void {
    let data = [...(this.filteredRevenueStageData || [])];
    if (this.revenueStageSortField) {
      const order = this.revenueStageSortOrder;
      const field = this.revenueStageSortField;
      data.sort((a: any, b: any) => {
        let va: any, vb: any;
        if (field === 'proposal_title') { va = (a.proposal_title ?? '').toString(); vb = (b.proposal_title ?? '').toString(); }
        else if (field === 'category') { va = (a.category ?? '').toString(); vb = (b.category ?? '').toString(); }
        else if (field === 'current_stage') { va = (this.getCurrentStage(a) ?? '').toString(); vb = (this.getCurrentStage(b) ?? '').toString(); }
        else if (field === 'overall_status') { va = (this.getOverallStatus(a) ?? '').toString(); vb = (this.getOverallStatus(b) ?? '').toString(); }
        else if (field === 'last_updated') {
          va = a.last_updated ? new Date(a.last_updated).getTime() : 0;
          vb = b.last_updated ? new Date(b.last_updated).getTime() : 0;
          return order * (va - vb);
        }
        else if (field === 'delay_days') {
          va = this.getDaysInCurrentStage(a) ?? 0;
          vb = this.getDaysInCurrentStage(b) ?? 0;
          return order * (Number(va) - Number(vb));
        }
        else { return 0; }
        const cmp = (va || '').localeCompare(vb || '', undefined, { sensitivity: 'base' });
        return order * cmp;
      });
    }
    const total = data.length;
    this.revenueStageTableTotalPages = Math.max(1, Math.ceil(total / this.revenueStageTablePageSize));
    const start = (this.revenueStageTableCurrentPage - 1) * this.revenueStageTablePageSize;
    const end = start + this.revenueStageTablePageSize;
    this.revenueStageTablePaginatedData = data.slice(start, end);
    this.revenueStageTableStartEntry = total ? start + 1 : 0;
    this.revenueStageTableEndEntry = Math.min(end, total);
  }

  get revenueStageTablePages(): number[] {
    return Array.from({ length: this.revenueStageTableTotalPages }, (_, i) => i + 1);
  }

  changeRevenueStagePage(page: number): void {
    if (page < 1 || page > this.revenueStageTableTotalPages) return;
    this.revenueStageTableCurrentPage = page;
    this.setupRevenueStagePagination();
  }

  changeRevenueStagePageSize(): void {
    this.revenueStageTableCurrentPage = 1;
    this.setupRevenueStagePagination();
  }

  applyRevenueManageFilter(): void {
    const f = this.revenueManageFilters;
    const list = this.filteredProposals || [];
    this.revenueManageFilteredProposals = list.filter((p: any) => {
      const name = (p.proposal_name ?? '').toString().toLowerCase();
      const nom = (p.nomenclature ?? '').toString().toLowerCase();
      const dateStr = (p.created_on ? new Date(p.created_on).toLocaleDateString('en-GB') : '').toLowerCase();
      if (f.proposal_name && !name.includes(f.proposal_name.trim().toLowerCase())) return false;
      if (f.nomenclature && !nom.includes(f.nomenclature.trim().toLowerCase())) return false;
      if (f.submitted_date && !dateStr.includes(f.submitted_date.trim().toLowerCase())) return false;
      return true;
    });
    this.revenueManageCurrentPage = 1;
    this.setupRevenueManagePagination();
  }

  sortRevenueManage(field: string): void {
    if (this.revenueManageSortField === field) {
      this.revenueManageSortOrder = (this.revenueManageSortOrder === 1 ? -1 : 1) as 1 | -1;
    } else {
      this.revenueManageSortField = field;
      this.revenueManageSortOrder = 1;
    }
    this.setupRevenueManagePagination();
  }

  setupRevenueManagePagination(): void {
    let data = [...(this.revenueManageFilteredProposals || [])];
    if (this.revenueManageSortField) {
      const order = this.revenueManageSortOrder;
      const field = this.revenueManageSortField;
      data.sort((a: any, b: any) => {
        let va: any, vb: any;
        if (field === 'proposal_name') { va = (a.proposal_name ?? '').toString(); vb = (b.proposal_name ?? '').toString(); }
        else if (field === 'nomenclature') { va = (a.nomenclature ?? '').toString(); vb = (b.nomenclature ?? '').toString(); }
        else if (field === 'submitted_date') {
          va = a.created_on ? new Date(a.created_on).getTime() : 0;
          vb = b.created_on ? new Date(b.created_on).getTime() : 0;
          return order * (va - vb);
        }
        else { return 0; }
        const cmp = (va || '').localeCompare(vb || '', undefined, { sensitivity: 'base' });
        return order * cmp;
      });
    }
    const total = data.length;
    this.revenueManageTotalPages = Math.max(1, Math.ceil(total / this.revenueManagePageSize));
    const start = (this.revenueManageCurrentPage - 1) * this.revenueManagePageSize;
    const end = start + this.revenueManagePageSize;
    this.revenueManagePaginatedData = data.slice(start, end);
    this.revenueManageStartEntry = total ? start + 1 : 0;
    this.revenueManageEndEntry = Math.min(end, total);
  }

  get revenueManagePages(): number[] {
    return Array.from({ length: this.revenueManageTotalPages }, (_, i) => i + 1);
  }

  changeRevenueManagePage(page: number): void {
    if (page < 1 || page > this.revenueManageTotalPages) return;
    this.revenueManageCurrentPage = page;
    this.setupRevenueManagePagination();
  }

  changeRevenueManagePageSize(): void {
    this.revenueManageCurrentPage = 1;
    this.setupRevenueManagePagination();
  }

  getProposal(tab: 'work_done' | 'work_on', page = 1) {
    this.proposalData = [];
    let url = `/master/work-details/proposal/?scheme_id=${this.schemeID}&tab=${tab}`;
    const sub = this.api.getAPI(url).subscribe((res: any) => {
      this.proposalData = res.data;
      this.totalCount = res.count;
    });
    this.subscriptions.add(sub);
  }

  onPageChange(event: any) {
    const rows = event.rows;
    const first = event.first;
    const page = first / rows + 1;
    this.getProposal(this.activeTab, page);
  }

  onTabChange(e: any) {
    this.activeTabIndex = e.index;

    if (e.index === 0) {
      this.getProposal('work_on', 1);
    } else if (e.index === 1) {
      this.getProposal('work_done', 1);
    }
  }

  // Handle proposal status tab change
  onProposalStatusTabChange(tab: any) {
    const tabIndex = tab.tab;
    this.activeStatusTabIndex = tab;

    if (tabIndex === 0) {
      this.getProposal(tab, 1);
    } else {
      this.getProposal(tab, 1);
    }
  }

  navigateToSchemeTransaction(schemeName: any, phaseId: any) {
    this.router.navigate(['/transaction'], {
      queryParams: {
        id: schemeName.id,
        name: schemeName.name,
        scheme: schemeName.scheme.id,
        phase: phaseId,
        step: schemeName.step_summary.current_step_id,
      },
    });
  }

  searchQuery: string = '';

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'approved':
        return 'bg-success';
      case 'pending':
        return 'bg-warning text-dark';
      case 'short_closed':
        return 'bg-secondary';
      case 'rejected':
        return 'bg-danger';
      default:
        return 'bg-info';
    }
  }

  getFieldValue(obj: any, fieldPath: string): any {
    if (!obj || !fieldPath) {
      return null;
    }
    try {
      return fieldPath.split('.').reduce((acc, part) => {
        if (acc && typeof acc === 'object' && part in acc) {
          return acc[part];
        }
        return undefined;
      }, obj);
    } catch {
      return undefined;
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.currentAction == 'edit') {
        this.proposalForm?.enable();
      } else {
        this.proposalForm?.disable();
      }
    }, 3000);
  }

  editorReady = true;
  rebuildEditor() {
    this.editorReady = false;
    setTimeout(() => {
      this.init = this.getTinyInit();
      this.editorReady = true;
    }, 0);
  }

  getTinyInit(): EditorComponent['init'] {
    return {
      plugins:
        'advlist anchor fullscreen insertdatetime autolink link lists image code advcode mergetags wordcount ai a11ychecker advcode advtemplate advlist advtable anchor autocorrect autosave editimage image link linkchecker lists markdown media mediaembed pageembed powerpaste searchreplace table template tinymcespellchecker typography visualblocks wordcount',
      toolbar:
        'undo redo fullscreen | styles fontsizeinput lineheight |insertdatetime  bold italic strikethrough backcolor forecolor visualblocks | align bullist numlist outdent indent | advlist  table link image media pageembed | spellcheckdialog a11ycheck code | searchreplace',
      readonly: this.currentAction === 'view',
    };
  }

  switchRole(role: string) {
    this.currentRole = role;
    this.applyFilters();
  }

  applyFilters() {
    let filtered = [...this.proposals];

    if (this.filters.category) {
      filtered = filtered.filter((p) => p.category === this.filters.category);
    }

    if (this.filters.priority) {
      filtered = filtered.filter((p) => p.priority === this.filters.priority);
    }
    
    if (this.filters.valuation) {
      filtered = filtered.filter((p) => p.valuation === this.filters.valuation);
    }
    
    if (this.filters.status) {
      // filtered = filtered.filter(p => p.status === this.filters.status);
    }

    this.filteredProposals = filtered;
    this.applyRevenueManageFilter();
    this.updateStats();
  }

  updateStats() {
    this.stats.total = this.filteredProposals.length;
  }

  updateCategory(id: any, category: string) {
    const p = this.proposals.find((x) => x.id == id);
    if (p) p.category = category;
    this.applyFilters();
  }

  updatePriority(id: any, priority: string) {
    // Implementation if needed
  }
  
  updateValuation(id: any, priority: string) {
    // Implementation if needed
  }
  
  updatelaunchStatus(id: any, priority: string) {
    // Implementation if needed
  }
  
  selectedChallengeCount: any[] = [];
  
  toggleSelectAll() {
    this.filteredProposals.forEach((proposal) => {
      proposal.selected = this.selectAll;
    });

    if (this.selectAll) {
      this.selectedChallengeCount = [...this.filteredProposals];
    } else {
      this.selectedChallengeCount = [];
    }
  }

  bulkAction(action: string) {
    // Implementation if needed
  }

  viewDetails(data: any, mode: 'view') {
    this.selectedProposal = data;
    this.apiCall();
    this.detailsModalOpen = true;
    this.currentAction = mode;
    this.isEditMode = false;
    this.rebuildEditor();
    this.proposalForm.disable();
    this.proposalForm.patchValue({
      nomenclature: data?.nomenclature,
      qtyReqd: data?.qty_req,
      qtyPrototype: data?.prototype_qty,
      qtyBulk: data?.bulk_qty,
      estimatedDevelopmentCost: data?.development_cost,
      estimatedUnitCost: data?.unit_cost,
      justificationText: data?.justification,
      exportPotential: data?.proposed_item,
      equipment: data?.equipment,
      system: data?.system,
      vendor: data?.vendor,
      consignee: data?.consignee,
      introduction: data?.introduction,
      proposal: data?.proposal_desc,
      summary: data?.summary,
      financialImplication: data?.financial_implication,
      justification: data?.justification,
      technologyInsertion: data?.technical_details,
      approvalOfSOTRs: data?.formulation_sotr,
      llp: data?.lpp_cost,
      poev: data?.poev,
    });
  }
  
  savetableData(data: any) {
    this.proposalForm.reset();
    this.selectedProposal = data;
    this.proposalForm.patchValue({
      nomenclature: data?.nomenclature,
      qtyReqd: data?.qty_req,
      qtyPrototype: data?.prototype_qty,
      qtyBulk: data?.bulk_qty,
      estimatedDevelopmentCost: data?.development_cost,
      estimatedUnitCost: data?.unit_cost,
      justificationText: data?.justification,
      exportPotential: data?.proposed_item,
      equipment: data?.equipment?.id,
      system: data?.system?.id,
      vendor: data?.vendor,
      consignee: data?.consignee,
      introduction: data?.introduction,
      proposal: data?.proposal_desc,
      summary: data?.summary,
      financialImplication: data?.financial_implication,
      justification: data?.justification,
      technologyInsertion: data?.technical_details,
      approvalOfSOTRs: data?.formulation_sotr,
      llp: data?.lpp_cost,
      poev: data?.poev,
    });
    this.onSubmit();
  }

  closeDetailsModal() {
    this.detailsModalOpen = false;
    this.selectedProposal = null;
  }

  editProposal(data: any, mode: 'edit') {
    this.currentAction = mode;
    console.log(data);
    this.rebuildEditor();
    this.isEditMode = true;
    this.apiCall();
    this.proposalForm.enable();

    this.selectedProposal = { ...data };
    this.proposalForm.reset();
    this.proposalForm.patchValue({
      nomenclature: data?.nomenclature,
      qtyReqd: data?.qty_req,
      qtyPrototype: data?.prototype_qty,
      qtyBulk: data?.bulk_qty,
      estimatedDevelopmentCost: data?.development_cost,
      estimatedUnitCost: data?.unit_cost,
      justificationText: data?.justification,
      exportPotential: data?.proposed_item,
      equipment: data?.equipment,
      system: data?.system,
      vendor: data?.vendor,
      consignee: data?.consignee,
      introduction: data?.introduction,
      proposal: data?.proposal_desc,
      summary: data?.summary,
      financialImplication: data?.financial_implication,
      justification: data?.justification,
      technologyInsertion: data?.technical_details,
      approvalOfSOTRs: data?.formulation_sotr,
      llp: data?.lpp_cost,
      poev: data?.poev,
    });
    this.detailsModalOpen = true;
  }
  
  gettableData() {
    let unit = Number(sessionStorage.getItem('unit_id'));
    this.api
      .getAPI(
        `/revenue/dashboard/need-generation/list/?role_id=${this.userID}&unit_id=${unit}`
      )
      .subscribe({
        next: (res: any[]) => {
          this.proposals = res;
          this.filteredProposals = [...this.proposals];
          this.applyRevenueManageFilter();
        },
      });
  }
  
  onFileSelect(event: any, uploadType: 'draftSotr' | 'supportingDocs') {
    if (uploadType === 'draftSotr') {
      this.draftSotr = event.files[0];
    } else {
      for (let file of event.files) {
        this.uploadedFiles.push(file);
      }
    }

    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'File(s) attached successfully',
    });
  }

  onUploadDraftSotr(event: any) {
    const file: File = event.files[0];
    if (file) {
      this.draftSotrFile = file;
      this.messageService.add({
        severity: 'success',
        summary: 'File attached successfully',
        detail: file.name,
      });
    }
  }

  onUploadSupportingDocs(event: any) {
    const file: File = event.files[0];
    if (file) {
      this.supportingDocsFiles = file;
    }
    this.messageService.add({
      severity: 'success',
      summary: 'Supporting Documents Uploaded',
      detail: ` files uploaded`,
    });
  }
  
  getFileName(url: string): string {
    const fileName = url.split('/').pop() || 'Unknown File';
    return fileName.length > 50 ? fileName.substring(0, 50) + '...' : fileName;
  }
  
  onSubmit() {
    const formData = new FormData();
    formData.append('equipment_id', this.proposalForm.value.equipment);
    formData.append('system_id', this.proposalForm.value.system);
    formData.append('consignee', this.proposalForm.value.consignee);
    formData.append('nomenclature', this.proposalForm.value.nomenclature);
    formData.append('qty_req', this.proposalForm.value.qtyReqd);
    formData.append('prototype_qty', this.proposalForm.value.qtyPrototype);
    formData.append('bulk_qty', this.proposalForm.value.qtyBulk);
    formData.append(
      'development_cost',
      this.proposalForm.value.estimatedDevelopmentCost
    );
    formData.append('unit_cost', this.proposalForm.value.estimatedUnitCost);
    formData.append('justification', this.proposalForm.value.justificationText);
    formData.append('proposed_item', this.proposalForm.value.exportPotential);
    formData.append(
      'formulation_sotr',
      this.proposalForm.value.approvalOfSOTRs
    );
    formData.append('lpp_cost', this.proposalForm.value.llp);
    formData.append('poev', this.proposalForm.value.poev);
    formData.append(
      'technical_details',
      this.proposalForm.value.technologyInsertion
    );

    if (this.draftSotr) {
      formData.append('draft_sotr', this.draftSotr);
    }

    if (this.uploadedFiles.length > 0) {
      this.uploadedFiles.forEach((file, index) => {
        formData.append(`uploaded_files`, file);
      });
    }
    
    console.log(formData, this.selectedProposal);
    let url = `/revenue/need-generation/${this.selectedProposal.id}/`;

    const sub = this.api.putAPI(url, formData).subscribe({
      next: (res: any) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Proposal Updated successfully',
        });
        this.gettableData();
        this.fileUpload.clear();
        this.draftSotrUpload.clear();
        if (res.draft_sotr) {
          this.selectedProposal.draft_sotr = res.draft_sotr;
        }

        if (res.need_files) {
          this.selectedProposal.need_files = res.need_files.map((f: any) => ({
            file: f.file,
            name: f.name ?? this.getFileName(f.file),
          }));
        }
      },
      error: (err) => {},
    });
    this.subscriptions.add(sub);
  }
  
  routeModal: boolean = false;
  
  openRouteModal() {
    this.routeModal = true;
    this.selectedChallenges = this.proposals.filter((p) => p.selected);
    if (this.userID != '3') {
      this.getRoleList();
      this.getUserList();
      this.getUnit();
    }

    if (this.selectedChallenges.length > 0) {
      this.routeModal = true;
    }
  }
  
  onApprove(proposal: any) {
    this.filteredProposals = this.filteredProposals.map((p) => ({
      ...p,
      isSelected: p === proposal,
    }));

    this.selectedChallenges = [proposal];
    this.routeModal = true;
  }

  onChallengeSelectChange(proposal: any) {
    if (proposal.selected) {
      if (!this.selectedChallengeCount.some((ch) => ch.id === proposal.id)) {
        this.selectedChallengeCount.push(proposal);
      }
    } else {
      this.selectedChallengeCount = this.selectedChallengeCount.filter(
        (ch) => ch.id !== proposal.id
      );
    }
  }

  onrouteFormSubmit() {
    const formValue = this.routeForm.value;
    const currentUserRole = sessionStorage.getItem('user_role_name');
  
    const selectedUser = this.userList.find(u => u.id === formValue.user);

    const payload = this.selectedChallenges.map((challenge, index) => ({
      proposal_step:
        currentUserRole?.toLowerCase() === 'approver'
          ? challenge.next_proposal_step_id
          : challenge.current_proposal_step,
      role: formValue.role,
      user: formValue.user,
      sequence: formValue.level,
      unit: selectedUser ? selectedUser.unit?.id : null,
      is_final_approve: formValue.approved === true,
    }));

    console.log('Payload:', payload);
    const sub = this.api
      .postAPI(`/idex/bulk/proposal-workflow/`, payload)
      .subscribe({
        next: (res: any) => {
          this.routeForm.reset();
          this.selectedChallenges = null;
          this.routeModal = false;
          this.gettableData();
        },
        error: (err) => {
          console.error('Update failed:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail:
              err?.error?.detail ||
              'Something went wrong while updating the mapping.',
          });
        },
      });
    this.subscriptions.add(sub);
  }
  
  submitIRA() {
    let role_id = sessionStorage.getItem('user_role_id');
    const payload = this.selectedChallenges.map((challenge, index) => ({
      proposal_step: challenge.current_proposal_step,
      role_id: Number(role_id),
      is_approved: true,
      remark: this.IRAremarks,
    }));
    this.IRAremarks = '';
    const sub = this.api
      .postAPI(`/idex/bulk/proposal-ira-action/`, payload)
      .subscribe((res: any) => {
        this.isEditable = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: res.message || 'Proposal added successfully',
        });
        if (this.userID != '3') {
          this.onrouteFormSubmit();
        } else {
          this.routeModal = false;
        }
      });
    this.subscriptions.add(sub);
  }
  
  getRoleList() {
    const currentUserRole = sessionStorage.getItem('user_role_name');

    const rolePriority = ['INT', 'REC', 'APP'];

    this.api.getAPI('/access/user-roles/').subscribe((res: any) => {
      const resp = res.data;

      let roles = resp.filter((role) => rolePriority.includes(role.code));

      if (
        currentUserRole?.toLowerCase() === 'initiator' &&
        this.filteredProposals[0].proposal_step_status == '0' &&
        this.filteredProposals[0].type == 'formulation challenge'
      ) {
        roles = roles.filter((r) => r.code === 'APP');
      } else if (
        currentUserRole?.toLowerCase() === 'initiator' &&
        this.filteredProposals[0].proposal_step_status == '0' &&
        this.filteredProposals[0].type == 'initiation challenge'
      ) {
        roles = roles.filter((r) => r.code === 'REC' || r.code === 'APP');
      } else if (
        currentUserRole?.toLowerCase() === 'initiator' &&
        this.filteredProposals[0].proposal_step_status == '0' &&
        this.filteredProposals[0].type == 'challenge approval'
      ) {
        roles = roles.filter((r) => r.code === 'APP');
      } else if (currentUserRole?.toLowerCase() === 'rec') {
        roles = roles.filter((r) => r.code === 'REC' || r.code === 'APP');
      } else if (currentUserRole?.toLowerCase() === 'approver') {
        roles = roles.filter((r) => r.code === 'INT');
      }

      this.roleList = roles.sort(
        (a, b) => rolePriority.indexOf(a.code) - rolePriority.indexOf(b.code)
      );
    });
  }

  getUserList() {
    const sub = this.api.getAPI('/account/users/').subscribe((res: any) => {
      this.userList = res.data;
      this.userListCopy = [...this.userList];
    });
    this.subscriptions.add(sub);
  }
  
  getUnit() {
    const sub = this.api.getAPI('/master/units/').subscribe((res: any) => {
      this.unitList = res.data;
    });
    this.subscriptions.add(sub);
  }
  
  onRoleChange(event: any) {
    this.tryFetchingSequence();
  }

  onUserChange(event: any) {
    this.tryFetchingSequence();
  }
  
  onUnitChange(event: any) {
    let unit_id = event.value;
    this.userList = this.userListCopy.filter(
      (user) => user.unit?.id == unit_id
    );
  }
  
  tryFetchingSequence() {
    const step = this.selectedChallenges[0].current_proposal_step;
    const role = this.routeForm.get('role').value;
    const user = this.routeForm.get('user').value;
    if (step && role && user) {
      this.getSequence();
    }
  }

  getSequence() {
    const step = this.selectedChallenges[0].current_proposal_step;
    const role = this.routeForm.get('role').value;
    const user = this.routeForm.get('user').value;
    const sub = this.api
      .getAPI(
        `/transaction/get-next-level/?proposal_step_id=${step}&role_id=${role}`
      )
      .subscribe((res: any) => {
        const sequenceValue = res.level ?? '';
        this.routeForm.get('level')?.patchValue(sequenceValue);
      });
    this.subscriptions.add(sub);
  }

  activeTab: 'work_done' | 'work_on' = 'work_on';
  selectedProposals: string[] = [];
  showReviewModal: boolean = false;
  showActionModal: boolean = false;

  milestoneproposals = [
    {
      id: 'PROP001',
      title: 'AI Research Initiative',
      status: 'Active',
      milestones: 5,
      progress: 60,
      nextDeadline: '2024-10-15',
    },
    {
      id: 'PROP002',
      title: 'Defense Technology Project',
      status: 'Active',
      milestones: 8,
      progress: 25,
      nextDeadline: '2024-09-20',
    },
    {
      id: 'PROP003',
      title: 'Cyber Security Enhancement',
      status: 'Review',
      milestones: 6,
      progress: 80,
      nextDeadline: '2024-09-30',
    },
  ];

  wbsVersions = [
    {
      version: 'v1.0',
      uploadDate: '2024-08-15',
      status: 'Active',
      uploader: 'John Doe',
    },
    {
      version: 'v1.1',
      uploadDate: '2024-08-25',
      status: 'Draft',
      uploader: 'Jane Smith',
    },
  ];

  tranchePayments = [
    {
      milestone: 'Milestone 1',
      percentage: 20,
      amount: 100000,
      dueDate: '2024-09-15',
      status: 'Completed',
    },
    {
      milestone: 'Milestone 2',
      percentage: 30,
      amount: 150000,
      dueDate: '2024-10-15',
      status: 'Pending',
    },
    {
      milestone: 'Milestone 3',
      percentage: 25,
      amount: 125000,
      dueDate: '2024-11-15',
      status: 'Upcoming',
    },
  ];

  setActiveTab(tab: 'work_done' | 'work_on') {
    this.activeTab = tab;
    this.getProposal(this.activeTab, 1);
  }

  toggleProposalSelection(id: string, checked: boolean) {
    if (checked) {
      this.selectedProposals.push(id);
    } else {
      this.selectedProposals = this.selectedProposals.filter((p) => p !== id);
    }
  }
  
  proposalSteps: { id: number; nextStep: number }[] = [];

  addProposalStep(proposal: any) {
    this.proposalSteps.push({
      id: proposal.id,
      nextStep: proposal.next_proposal_step_id,
    });
  }
  
  openFullscreen(chartId: string) {
    const chartsMap: any = {
      chart1: { type: 'bar', data: this.chartData1 },
      chart2: { type: 'bar', data: this.chartData2 },
      chart3: { type: 'line', data: this.chartData3 },
      chart4: { type: 'pie', data: this.chartData4 },
      chart5: { type: 'doughnut', data: this.chartData5 },
      chart6: { type: 'bar', data: this.chartData6 },
      stageProgress: {
        type: 'bar',
        data: this.stageProgressChart,
        options: this.stageProgressChartOptions,
      },
      categoryDoughnut: { type: 'doughnut', data: this.categoryDoughnutChart, options: this.doughnutOptions },
      statusDoughnut: { type: 'doughnut', data: this.statusDoughnutChart, options: this.doughnutOptions },
      approvalFunnel: { type: 'bar', data: this.approvalFunnelChart, options: this.funnelBarOptions },
      executionProgress: {
        type: 'bar',
        data: this.executionProgressChart,
        options: this.executionProgressChartOptions,
      },
      delay: { type: 'doughnut', data: this.delayChart, options: this.doughnutOptions },
    };
    const c = chartsMap[chartId];
    this.fullscreenChart = c ? { type: c.type, data: c.data, options: c.options } : { type: 'bar', data: this.chartData1 };
    this.fullscreenVisible = true;
  }
  
  navigateto() {
    this.router.navigate(['/scheme/revenue'], {
      queryParams: { scheme_id: this.schemeID },
    });
  }
  
  onYearChange(year: number) {
    this.getDashboardData(year);
  }
  
  getDashboardData(year: number) {
    this.api
      .getAPI(
        `/idex/dashboard/details/?scheme_id=${this.schemeID}&year=${year}`
      )
      .subscribe({
        next: (res: any) => {
          // Chart 1: Year-wise Proposals vs Completed
          const yearlyData = res.yearly_received_vs_completed['2025'] || [];
          this.chartData1 = {
            labels: yearlyData.map((d: any) => d.scheme_name),
            datasets: [
              {
                label: 'Received',
                backgroundColor: '#42A5F5',
                data: yearlyData.map((d: any) => d.received),
              },
              {
                label: 'Approved',
                backgroundColor: '#66BB6A',
                data: yearlyData.map((d: any) => d.approved),
              },
              {
                label: 'Rejected',
                backgroundColor: '#EF5350',
                data: yearlyData.map((d: any) => d.rejected),
              },
            ],
          };

          // Chart 2: Schemes - Received/In Progress/Completed
          const schemeStatus = res.schemes_proposal_status || [];
          this.chartData2 = {
            labels: schemeStatus.map((d: any) => d.scheme__name),
            datasets: [
              {
                label: 'Received',
                backgroundColor: '#42A5F5',
                data: schemeStatus.map((d: any) => d.received),
              },
              {
                label: 'In Progress',
                backgroundColor: '#FFB300',
                data: schemeStatus.map((d: any) => d.in_progress),
              },
              {
                label: 'Completed',
                backgroundColor: '#66BB6A',
                data: schemeStatus.map((d: any) => d.completed),
              },
              {
                label: 'Rejected',
                backgroundColor: '#EF5350',
                data: schemeStatus.map((d: any) => d.rejected),
              },
            ],
          };

          // Chart 3: Funds Utilised
          this.chartData3 = {
            labels: ['2021', '2022', '2023', '2024', '2025'],
            datasets: [
              {
                label: 'Funds Utilised',
                borderColor: '#42A5F5',
                fill: false,
                data: [100, 200, 150, 300, 400],
                tension: 0,
              },
            ],
          };

          // Chart 6: Proposal Value vs Disbursed Funds
          this.chartData6 = {
            labels: ['Scheme A', 'Scheme B', 'Scheme C'],
            datasets: [
              {
                label: 'Proposal Value',
                backgroundColor: '#42A5F5',
                data: [300, 200, 400],
              },
              {
                label: 'Disbursed Funds',
                backgroundColor: '#66BB6A',
                data: [200, 150, 350],
              },
            ],
          };

          // Chart 4: Proposals Detailed View
          const proposals = res.proposals_detail || [];
          const statusCount: any = {};
          proposals.forEach((p: any) => {
            statusCount[p.proposal_status] =
              (statusCount[p.proposal_status] || 0) + 1;
          });
          this.chartData4 = {
            labels: Object.keys(statusCount),
            datasets: [
              {
                data: Object.values(statusCount),
                backgroundColor: ['#42A5F5', '#66BB6A', '#FFB300', '#EF5350'],
              },
            ],
          };

          // Chart 5: Sub-module Wise Proposal Distribution
          const schemeCount: any = {};
          proposals.forEach((p: any) => {
            schemeCount[p.scheme__name] =
              (schemeCount[p.scheme__name] || 0) + 1;
          });
          this.chartData5 = {
            labels: Object.keys(schemeCount),
            datasets: [
              {
                data: Object.values(schemeCount),
                backgroundColor: ['#42A5F5', '#66BB6A', '#FFB300', '#AB47BC'],
              },
            ],
          };
          
          // compute counts
          const totalReceived = schemeStatus.reduce(
            (sum, s) => sum + (s.received || 0),
            0
          );
          const totalInProgress = schemeStatus.reduce(
            (sum, s) => sum + (s.in_progress || 0),
            0
          );
          const totalCompleted = schemeStatus.reduce(
            (sum, s) => sum + (s.completed || 0),
            0
          );
          const totalRejected = schemeStatus.reduce(
            (sum, s) => sum + (s.rejected || 0),
            0
          );

          this.statusCards = [
            {
              label: 'Total Proposals',
              count: totalReceived,
              icon: 'pi pi-inbox',
              color: '#007bff',
            },
            {
              label: 'In Progress',
              count: totalInProgress,
              icon: 'pi pi-spin pi-spinner',
              color: '#ffc107',
            },
            {
              label: 'Completed',
              count: totalCompleted,
              icon: 'pi pi-check-circle',
              color: '#28a745',
            },
            {
              label: 'Rejected',
              count: totalRejected,
              icon: 'pi pi-times-circle',
              color: '#dc3545',
            },
          ];
        },
      });
  }
  
  allowOnlyNumbers(event: KeyboardEvent) {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight'];
    if (allowedKeys.includes(event.key)) return;

    const isNumber = /^[0-9]$/.test(event.key);
    if (!isNumber) {
      event.preventDefault();
    }
  }
  
  apiCall() {
    this.api.getAPI('/master/system-master/').subscribe((res: any) => {
      this.system = res.data;
      this.filteredSystem = res.data;
    });
    this.api.getAPI('/master/equipment-master/').subscribe((res: any) => {
      this.equipment = res.data;
      this.filteredEquipment = res.data;
    });
  }
}

interface Equipment {
  id: number;
  name: string;
}

interface System {
  id: number;
  name: string;
}